import { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import type { Audit } from '../../lib/api';
import { useFormatters } from '../../contexts/PreferencesContext';
import { Badge } from '../../components/ui/badge';
import { Button } from '../../components/ui/button';
import { Card, CardContent } from '../../components/ui/card';
import { Pagination } from '../../components/ui/pagination';
import { Table, TableBody, TableCell, TableHeader, TableRow } from '../../components/ui/table';
import { SortableTh } from '../../components/common/SortableTh';
import { AlertCircle, ClipboardCheck, Loader2, Pencil } from 'lucide-react';
import { useColumnSort } from '../../hooks/useColumnSort';
import AuditArchiveMenu from '../audit-detail/AuditArchiveMenu';
import EditAuditDetailsDialog from '../audit-detail/EditAuditDetailsDialog';
import { AUDIT_ARCHIVED, AUDIT_OUTCOME, AUDIT_STATUS } from './auditStatus';
import { rowOpens } from './rowClick';
import { useAuditRowActions } from './useAuditRowActions';

interface AuditListProps {
  audits: Audit[];
  loading: boolean;
  error: string | null;
  onRetry: () => void;
  /** Supplier profile: the supplier is implied, so its column is dropped. */
  hideSupplier?: boolean;
  emptyText: string;
  /** Shown under the empty text (e.g. a Start audit button). */
  emptyAction?: React.ReactNode;
  /** Reload after a row edit / archive / restore / delete. Without it the row actions are hidden. */
  onChanged?: () => void;
}

const PAGE_SIZE = 25;
const TH = 'h-10 px-3 text-sm font-medium text-muted-foreground whitespace-nowrap';
/** Narrow count columns: the header may wrap onto two lines rather than be cut off. */
const TH_COUNT = 'h-10 px-3 text-sm font-medium text-muted-foreground w-[6rem]';
/** Text columns wrap at word boundaries but never get squeezed below a readable width. */
const WRAP = 'align-top break-words';

/** Sort keys for columns whose cell shows a label rather than the raw field. */
const SORT_KEYS: Record<string, (a: Audit) => unknown> = {
  audit: (a) => (a.name || a.template_name).toLowerCase(),
  supplier_name: (a) => a.supplier_name.toLowerCase(),
  template_name: (a) => a.template_name.toLowerCase(),
  status: (a) => (a.archived ? 'zz archived' : AUDIT_STATUS[a.status].label),
  outcome: (a) => (a.outcome ? AUDIT_OUTCOME[a.outcome].label : null),
  lead_auditor_name: (a) => a.lead_auditor_name?.toLowerCase() || null,
};


/**
 * Audits table: the audit's name first, then supplier, template, status,
 * outcome, auditor, dates, validity, findings and open actions. Every column sorts;
 * pagination (with Rows) above and below; per row a quick Edit (name, date, …) and
 * a menu with Edit / Archive or Restore / Delete, as the caller is allowed.
 */
export default function AuditList({
  audits, loading, error, onRetry, hideSupplier = false, emptyText, emptyAction, onChanged,
}: AuditListProps) {
  const navigate = useNavigate();
  const { formatDate } = useFormatters();
  const [page, setPage] = useState<number>(1);
  const [pageSize, setPageSize] = useState<number>(PAGE_SIZE);
  const [editing, setEditing] = useState<Audit | null>(null);
  const actions = useAuditRowActions(onChanged ?? (() => undefined));
  const { sortedRows, sortField, sortDirection, onSort } = useColumnSort(audits, {
    computed: SORT_KEYS,
    dateFields: ['audit_date', 'completed_at', 'valid_until'],
    numericFields: ['findings_count', 'open_actions_count'],
  });

  // A new search / filter / reload starts at page 1, and a page past the end snaps back.
  const totalPages = Math.max(1, Math.ceil(sortedRows.length / pageSize));
  useEffect(() => { setPage(1); }, [audits]);
  useEffect(() => { if (page > totalPages) setPage(totalPages); }, [page, totalPages]);
  const pageRows = useMemo(() => sortedRows.slice((page - 1) * pageSize, page * pageSize), [sortedRows, page, pageSize]);

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12 text-muted-foreground">
        <Loader2 className="h-5 w-5 animate-spin mr-2" /> Loading audits…
      </div>
    );
  }
  if (error) {
    return (
      <Card>
        <CardContent className="flex items-center justify-between gap-4 py-6">
          <span className="flex items-center gap-2 text-sm text-destructive">
            <AlertCircle className="h-4 w-4" /> {error}
          </span>
          <Button variant="outline" size="sm" onClick={onRetry}>Retry</Button>
        </CardContent>
      </Card>
    );
  }
  if (audits.length === 0) {
    return (
      <Card>
        <CardContent className="py-12 text-center space-y-3">
          <ClipboardCheck className="h-10 w-10 mx-auto text-muted-foreground" />
          <p className="text-muted-foreground">{emptyText}</p>
          {emptyAction}
        </CardContent>
      </Card>
    );
  }

  const pager = (className?: string) => (
    <Pagination currentPage={page} totalPages={totalPages} totalCount={sortedRows.length} itemsPerPage={pageSize}
      onPageChange={setPage} onPageSizeChange={(size) => { setPageSize(size); setPage(1); }} itemLabel="audits"
      className={className} />
  );
  const sortable = { sortField, sortDirection, onSort };

  return (
    <div className="space-y-2">
      {totalPages > 1
        ? pager('border-t-0 pt-0 mt-0 border-b pb-3 mb-1')
        : <p className="text-sm text-muted-foreground">{sortedRows.length} {sortedRows.length === 1 ? 'audit' : 'audits'}</p>}
      <div className="rounded-md border overflow-x-auto">
        <Table>
          <TableHeader>
            <TableRow>
              <SortableTh field="audit" {...sortable} className={`${TH} min-w-[15rem]`}>Audit</SortableTh>
              {!hideSupplier && <SortableTh field="supplier_name" {...sortable} className={`${TH} min-w-[10rem]`}>Supplier</SortableTh>}
              <SortableTh field="template_name" {...sortable} className={`${TH} min-w-[12rem]`}>Template</SortableTh>
              <SortableTh field="status" {...sortable} className={TH}>Status</SortableTh>
              <SortableTh field="outcome" {...sortable} className={TH}>Outcome</SortableTh>
              <SortableTh field="lead_auditor_name" {...sortable} className={TH}>Lead auditor</SortableTh>
              <SortableTh field="audit_date" {...sortable} className={TH}>Audit date</SortableTh>
              <SortableTh field="valid_until" {...sortable} className={TH}>Completed</SortableTh>
              <SortableTh field="findings_count" {...sortable} align="right" className={TH_COUNT}>Findings</SortableTh>
              <SortableTh field="open_actions_count" {...sortable} align="right" className={TH_COUNT}>Open actions</SortableTh>
            </TableRow>
          </TableHeader>
          <TableBody>
            {pageRows.map((audit) => {
              const status = AUDIT_STATUS[audit.status];
              const outcome = audit.outcome ? AUDIT_OUTCOME[audit.outcome] : null;
              const canEdit = Boolean(onChanged && audit.can_edit);
              const canArchive = Boolean(onChanged && audit.can_archive);
              return (
                <TableRow key={audit.id} className="cursor-pointer" tabIndex={0}
                  aria-label={`Open ${audit.name || audit.template_name} – ${audit.supplier_name}`}
                  onClick={(e) => { if (rowOpens(e)) navigate(`/audits/${audit.id}`); }}
                  onKeyDown={(e) => { if (e.key === 'Enter' && e.target === e.currentTarget) navigate(`/audits/${audit.id}`); }}>
                  <TableCell className={`${WRAP} min-w-[15rem] max-w-[22rem]`}>
                    <div className="flex items-start gap-1">
                      <button type="button" className="min-w-0 text-left hover:underline" onClick={() => navigate(`/audits/${audit.id}`)}>
                        <span className="block font-medium">{audit.name || audit.template_name}</span>
                      </button>
                      <span className="ml-auto flex shrink-0 items-center">
                        {canEdit && (
                          <Button type="button" size="icon" variant="ghost" className="h-8 w-8"
                            aria-label={`Edit ${audit.name || audit.template_name}`} title="Edit name and details"
                            onClick={() => setEditing(audit)}>
                            <Pencil className="h-4 w-4" />
                          </Button>
                        )}
                        {(canEdit || canArchive) && (
                          <AuditArchiveMenu audit={audit} compact canArchive={canArchive}
                            onEdit={canEdit ? () => setEditing(audit) : undefined}
                            onArchive={() => actions.archive(audit.id)} onRestore={() => actions.restore(audit.id)}
                            onDelete={() => actions.remove(audit.id)} />
                        )}
                      </span>
                    </div>
                  </TableCell>
                  {!hideSupplier && <TableCell className={`${WRAP} min-w-[10rem] max-w-[15rem]`}>{audit.supplier_name}</TableCell>}
                  <TableCell className={`${WRAP} min-w-[12rem] max-w-[16rem] text-sm text-muted-foreground`}>{audit.template_name}</TableCell>
                  <TableCell className="align-top whitespace-nowrap">
                    <Badge variant="outline" className={status.className}>{status.label}</Badge>
                    {audit.archived && (
                      <Badge variant="outline" className={`ml-2 ${AUDIT_ARCHIVED.className}`}>{AUDIT_ARCHIVED.label}</Badge>
                    )}
                    {audit.status === 'in_progress' && (
                      <span className="ml-2 text-xs text-muted-foreground">{audit.completion_percentage}%</span>
                    )}
                  </TableCell>
                  <TableCell className="align-top whitespace-nowrap">
                    {outcome ? <Badge variant="outline" className={outcome.className}>{outcome.label}</Badge> : '—'}
                  </TableCell>
                  <TableCell className={`${WRAP} min-w-[9rem]`}>{audit.lead_auditor_name || '—'}</TableCell>
                  <TableCell className="align-top whitespace-nowrap">{formatDate(audit.audit_date)}</TableCell>
                  <TableCell className="align-top whitespace-nowrap">
                    {audit.completed_at ? formatDate(audit.completed_at) : '—'}
                    {audit.completed_at && (
                      <span className="block text-xs text-muted-foreground">
                        {audit.valid_until ? `valid until ${formatDate(audit.valid_until)}` : 'no expiry'}
                      </span>
                    )}
                  </TableCell>
                  <TableCell className="align-top text-right">{audit.findings_count}</TableCell>
                  <TableCell className="align-top text-right whitespace-nowrap">
                    {audit.open_actions_count}
                    {audit.overdue_actions_count > 0 && (
                      <Badge variant="outline" className="ml-2 bg-red-100 text-red-800 dark:bg-red-950 dark:text-red-200">
                        {audit.overdue_actions_count} overdue
                      </Badge>
                    )}
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </div>
      {totalPages > 1 && pager()}
      {editing && (
        <EditAuditDetailsDialog open audit={editing} onOpenChange={(open) => { if (!open) setEditing(null); }}
          onSave={(payload) => actions.updateDetails(editing.id, payload)} />
      )}
    </div>
  );
}
